import { types, getParent, flow, getRoot } from 'mobx-state-tree';
import { when } from 'mobx';
import {
  MyLearning,
  MiniDashboard,
  SanityCheck,
  EbayData,
  DailyProfit,
  Revenue,
} from '../../models/Dashboard';

import WSMessage from '../../websockets/wsMessage';
import websockets from '../../websockets';

const URL = 'dashboard-service/v1';

const DashboardStore = types
  .model('dashboardStore', {
    initialized: types.optional(types.boolean, false),
    myLearning: types.maybe(MyLearning),
    miniDashboard: types.maybe(MiniDashboard),
    sanityChecks: types.optional(types.array(SanityCheck), []),
    ebayData: types.optional(EbayData, {}),
    sanityCheckOpen: types.optional(types.boolean, false),
    selectedSellerAccountId: types.optional(types.string, ''),
    dailyProfit: types.optional(DailyProfit, {}),
    revenue: types.optional(Revenue, {}),
    advancedMode: true,
    isMiniDashboardLoading:false,
    areListingsLoading:false,
    listings: types.optional(types.number, 0),
  })
  .views(self => ({
    // get advancedMode() {
    //   return process.env.REACT_APP_ENV === 'dev';
    // },
    get sellersAccounts() {
      return getParent(self).discoveryStore.sellersAccounts;
    },
    get settingsInitialized() {
      return getParent(self).settingsStore.initialized;
    },
    // get mappedSellersAccounts() {
    //   const accountMap = {}
    //   self.sellersAccounts.forEach(({id, name})=>{accountMap[id] = {id,name}});
    //   return accountMap;
    // },
    get transport5007() {
      return getParent(self).transport5007;
    },
    get accountCode() {
      return getParent(self).sessionStore.accountCode;
    },
    get mainColor() {
      // if (self.sanityChecks.length === 0) {
      //   return 1;
      // }
      // const filtered = self.sanityChecks.filter(elem => elem.statusType !== 0);
      // if (filtered.length === self.sanityChecks.length) {
      //   return 3;
      // } else if (filtered.length > 0) {
      //   return 2;
      // }
      return 0;
    },
    get someRedAlert() {
      return false;
      // return self.sanityChecks.some(elem => elem.statusType === 3);
    },
  }))
  .actions(self => ({
    initStore: flow(function* initStore() {
      websockets.addListener(WSMessage.Stores.Dashboard, self.rtUpdate);
      if (self.initialized === true) {
        return;
      }
      try {
        yield Promise.allSettled([
          // commented ones were moved in userProfile due to multiregion
          // they require the defaultCurrency from the API which isn't initialised at this point

          // self.fetchMiniDashboard(),
          self.fetchSanityCheck(),
          self.fetchRevenue(),
          self.fetchMyLearning(),
          // self.fetchDailyProfit();
        ]);
        yield when(() => self.settingsInitialized);
        yield self.fetchEbayData();
      } catch (error) {
        console.error('Getting DashboardStore data failed: ', error);
      } finally {
        self.initialized = true;
      }
    }),
    rtUpdate(objId, prop, value) {
      if (objId === WSMessage.DashboardObjects.MiniDashboard) {
        if (self.miniDashboard) WSMessage.updateObject(self.miniDashboard, prop, value);
      }
      if (objId === WSMessage.DashboardObjects.SanityCheck) {
        if (self.sanityChecks) WSMessage.updateObject(self.sanityChecks, prop, value);
      }
      if (objId === WSMessage.DashboardObjects.MyLearning) {
        if (self.myLearning) WSMessage.updateObject(self.myLearning, prop, value);
      }
      if (objId === WSMessage.DashboardObjects.EbayData) {
        if (self.ebayData) WSMessage.updateObject(self.ebayData, prop, value);
      }
    },
    fetchMyLearning: flow(function* fetchMyLearning() {
      try {
        const { data } = yield self.transport5007.fetch(`${URL}/MyLearning`, {}, {});
        self.myLearning = MyLearning.create(data);
        yield self.myLearning.fetchPlaylistTitle();
      } catch (error) {
        console.error('Getting MyLearning failed: ', error);
      }
    }),
    fetchMiniDashboard: flow(function* fetchMiniDashboard() {
      const currency = getParent(self).settingsStore.userProfile.defaultCurrencyId;
      const marketplace = getParent(self).settingsStore.userProfile.defaultSourceMarketplace;
      try {
        self.isMiniDashboardLoading=true;
        const { data } = yield self.transport5007.fetch(
          `${URL}/MiniDashboard?currency=${currency}&sourceMarketplace=${marketplace}`,
          {},
          {},
        );
        self.miniDashboard = MiniDashboard.create(data);
        self.isMiniDashboardLoading=false;
      } catch (error) {
        console.error('Getting MiniDashboard failed: ', error);
        self.isMiniDashboardLoading=false;
      }
    }),
    fetchListingCount: flow(function* fetchListingCount() {
      const marketplace = getParent(self).settingsStore.userProfile.defaultSourceMarketplace;
      try {
        self.areListingsLoading=true;
        const { data } = yield self.transport5007.fetch(
          `seller-listing/v1/Items/items-count?sourceMarketplaceId=${marketplace}`,
          {},
          {},
        );
        self.listings = data;
        self.areListingsLoading=false;
      } catch (error) {
        console.error('Getting ListingsCount failed: ', error);
        self.areListingsLoading=false;
      }
    }),
    fetchSanityCheck: flow(function* fetchSanityCheck() {
      try {
        const { data } = yield self.transport5007.fetch(
          `${URL}/MiniDashboard/SanityChecks`,
          {},
          {},
        );
        self.sanityChecks = data.map(elem =>
          SanityCheck.create(
            Object.assign({}, elem, {
              statusError: elem.statusError === null ? '' : elem.statusError,
            }),
          ),
        );
      } catch (error) {
        console.error('Getting MiniDashboard failed: ', error);
      }
    }),
    fetchDailyProfit: flow(function* fetchDailyProfit() {
      try {
        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const currency = getRoot(self).uiStore.settingsStore.userProfile.defaultCurrencyId;
        const sourceMarketplace = getRoot(self).uiStore.settingsStore.userProfile.defaultSourceMarketplace;
        const { data } = yield self.transport5007.fetch(`${URL}/Order`, { timeZoneId, currency, sourceMarketplace });
        self.dailyProfit = data;
      } catch (error) {
        console.error('Getting DailyProfit data failed: ', error);
      }
    }),
    fetchRevenue: flow(function* fetchRevenue() {
      try {
        const { data } = yield self.transport5007.fetch(`${URL}/Order/Revenue`);
        self.revenue = data;
      } catch (error) {
        console.error('Getting Revenue data failed: ', error);
      }
    }),
    fetchEbayData: flow(function* fetchEbayData() {
      try {
        if (self.selectedSellerAccountId !== '') {
          const { data } = yield self.transport5007.fetch(
            `${URL}/Ebay`,
            {},
            {
              'Seller-Account-Id': self.selectedSellerAccountId,
            },
          );
          self.ebayData = data;
        } else {
          // should happen only on the first load
          const account = self.sellersAccounts ? self.sellersAccounts[0] : null;
          if (account) {
            self.setSelectedSellerAccountId(account.id);
          }
          const { data } = yield self.transport5007.fetch(
            `${URL}/Ebay`,
            {},
            {
              'Seller-Account-Id': self.selectedSellerAccountId,
            },
          );
          self.ebayData = data;
        }
      } catch (error) {
        console.error('Getting Ebay Data failed: ', error);
      }
    }),
    setSanityCheckOpen(val) {
      self.sanityCheckOpen = !!val;
    },
    setSelectedSellerAccountId(val) {
      if (typeof val === 'string') {
        self.selectedSellerAccountId = val;
      }
    },
    cleanUpEbayData() {
      self.ebayData = {
        currentSellerLevel: '',
        feedbackScore: 0,
        feedbackTotalNegative: 0,
        feedbackTotalNeutral: 0,
        feedbackTotalPositive: 0,
        monthlyCreditsNumber: 0,
        monthlyCurrentSoldMoney: 0,
        monthlyForecastUploadedNumber: 0,
        monthlySellingLimitMoney: 0,
        monthlySellingLimitNumber: 0,
        monthlyUploadedNumber: 0,
        negativeSellerReview: 0,
        neutralSellerReview: 0,
        positiveSellerReview: 0,
        storeSubscription: null,
        casesWithoutResolutionNumber: 0,
        positiveFeedbackPercentage: 0,
      };
    },
  }));

export default DashboardStore;
